import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "antd";
import qs from "qs";
import { useStores } from "@/store";
import { getQuery } from "@/service/http";
import { Button } from '../../../components'


const DRAWER_HISTORY_MARKER = "booking-qrcode-detail-drawer";

/** 预订管理页地址，供 Drawer 内的 iframe 使用 */
export function buildBookingDetailUrl({
  mode,
  restaurantId,
  reservationCode,
  reservationId,
  lang,
  privateToken,
  source,
}) {
  if (!restaurantId) return "";

  const domain =
    mode === "PRODUTION"
      ? "https://webform.diningcity.asia"
      : "https://staging-webform.diningcity.asia";
  const query = qs.stringify({
    reservation_code: reservationCode,
    reservation_id: reservationId,
    lang,
    private_token: privateToken,
    source,
    show_group_booking: true,
  });

  return `${domain}/#/restaurants/${restaurantId}/reservation/manage?${query}`;
}

/**
 * Booking qrcode页面
 *
 */
const Main = () => {
  const { t } = useTranslation();
  const { h5Store, memberStore } = useStores();
  const [bookingDetail, setBookingDetail] = useState({})
  const { book_id, reservation_code, lang, source } = getQuery();
  const [scanned, setScanned] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const timer = useRef(null)
  const detailUrl = useMemo(
    () =>
      buildBookingDetailUrl({
        mode: process.env.REACT_APP_MODE,
        restaurantId: bookingDetail.restaurant?.id,
        reservationCode: reservation_code,
        reservationId: book_id,
        lang: lang || "en",
        privateToken: memberStore.private_token || window.private_token,
        source,
      }),
    [
      bookingDetail.restaurant,
      reservation_code,
      book_id,
      lang,
      source,
      memberStore.private_token,
    ]
  );

  useEffect(()=>{
    document.querySelector('.ui-headbar').setAttribute('style','display: none');
    document.querySelector('.page-main').setAttribute('style','padding-top:0px');

  })
  /* 轮训扫码状态 */
  const getQrcodeStatus = async () => {
    if (bookingDetail.qr_code?.key) {
      let res = await h5Store.getQrcodeStatus(bookingDetail.qr_code.key)
      if (res?.is_scanned) {
        setScanned(true)
        // 扫码成功后，轮询订单详情
        clearInterval(timer.current)
      }
    }
  }

  useEffect(() => {
    (async () => {
      let res = await h5Store.getBookingById(reservation_code, book_id)
      if (res && !res.qr_code) {
        let qrcode = await h5Store.createBookingQrcode(book_id)
        if (qrcode) {
          res.qr_code = qrcode
        }
      }
      setBookingDetail({ ...res })
    })()
  }, []);

  useEffect(() => {
    if (bookingDetail && bookingDetail.id) {
      timer.current = setInterval(() => {
        getQrcodeStatus()
      }, 3000);
    }
    return () => {
      clearInterval(timer.current)
    }
  }, [bookingDetail])

  useEffect(() => {
    if (!detailVisible || !window.iframeResize) return undefined;

    window.iframeResize.create("#web_form");
    return () => {
      window.iframeResize.destroy();
    };
  }, [detailVisible]);

  /**
   * 详情 iframe 内部的“返回”会触发浏览器的 history.back()，
   * 由于外层与 iframe 共享同一条 joint session history，
   * 这里在打开 Drawer 时插入一条哨兵历史记录并监听 popstate，
   * 使该返回操作只关闭 Drawer，而不会把外层页面也带着回退。
   */
  useEffect(() => {
    if (!detailVisible) return undefined;

    window.history.pushState({ [DRAWER_HISTORY_MARKER]: true }, "");

    const handlePopState = () => {
      setDetailVisible(false);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [detailVisible]);

  const closeDetailDrawer = () => {
    setDetailVisible(false);
    if (window.history.state?.[DRAWER_HISTORY_MARKER]) {
      window.history.back();
    }
  };

  return (
    <div className="booking-qrcode">
      <h3 className="title">{t("RESERVATION_INFO.QRCodeTitle")}</h3>
      <div className="res-info">
        <div className="name">
          <i className="icon icon-rest"></i>
          <div>{bookingDetail.restaurant && bookingDetail.restaurant.name}</div>
        </div>
        <div className="time">
          <i className="icon icon-time"></i>
          {bookingDetail.formated_time}{bookingDetail.formated_time?.split(':')[0] >= 12 ? 'PM' : 'AM'}
        </div>
      </div>
      <div className="booking-qrcode-inner">
        <div className="tip">{t("RESERVATION_INFO.QRCodeTip")}</div>
        <div className="success">
          {
            scanned && (
              <div className="success-text">
                <i className="icon icon-success"></i>
                {t("RESERVATION_INFO.ScanSuccessfully")}
              </div>
            )
          }
        </div>
        <div className="qr-code">
          <img src={bookingDetail.qr_code?.qrcode_url} mode="scaleToFill" lazy-load="false" />
        </div>
      </div>
      <div className="booking-qrcode-detail-footer">
        <Button
          disabled={!detailUrl}
          onClick={() => setDetailVisible(true)}
        >
          {t("MANAGE_RESERVATION")} 
        </Button>
      </div>
      <Drawer
        className="booking-qrcode-detail-drawer"
        placement="right"
        width="100%"
        destroyOnClose
        closable={false}
        keyboard
        maskClosable
        onClose={closeDetailDrawer}
        visible={detailVisible}
      >
        <div className="booking-qrcode-detail-header">
          <span>{t("Details")}</span>
          <button
            type="button"
            className="booking-qrcode-detail-close"
            aria-label={`${t("VOUCHERS.Close")} ${t("Details")}`}
            onClick={closeDetailDrawer}
          >
            <i className="icon icon-close"></i>
          </button>
        </div>
        {detailVisible && (
          <div className="booking-qrcode-detail-frame">
            <iframe
              id="web_form"
              title={t("RESERVATION_DETAIL")}
              src={detailUrl}
              frameBorder="0"
            ></iframe>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Main;
